import React, {useState} from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@material-ui/core';
import {makeStyles} from '@material-ui/core/styles';
import Api from '../../../../store/Api';
import {DetailedCandidate, LlmCandidateState, LlmConfig} from '../../../../store/models/DuplicateCheck';
import {parseDetailedJson, splitLlmCandidates} from '../../../../util/DuplicateCheckUtil';
import {GREY, WHITE} from '../../../../BerendsohnMUITheme';
import {StatusBadge} from './shared';
import LlmPanel from './LlmPanel';
import {LLM_MAX_CANDIDATES, LLM_PATTERN_LABELS, LLM_VERDICT_COLORS, LLM_VERDICT_LABELS} from './constants';

const useStyles = makeStyles((theme) => ({
    editor: {
        fontFamily: 'monospace',
        fontSize: 13,
    },
    actions: {
        display: 'flex',
        alignItems: 'flex-end',
        gap: theme.spacing(2),
        marginTop: theme.spacing(2),
    },
    errorText: {
        color: theme.palette.error.main,
    },
    verdictChip: {
        color: WHITE,
        fontWeight: 'bold',
    },
    reasoning: {
        whiteSpace: 'pre-wrap',
    },
}));

interface LlmJsonCheckPanelProps {
    llmConfig: LlmConfig | null;
    llmPreset: string | undefined;
    onLlmPresetChange: (preset: string) => void;
}

export default function LlmJsonCheckPanel({llmConfig, llmPreset, onLlmPresetChange}: LlmJsonCheckPanelProps) {
    const classes = useStyles();
    const [text, setText] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [candidates, setCandidates] = useState<DetailedCandidate[]>([]);
    const [results, setResults] = useState<Record<string, LlmCandidateState>>({});
    const [models, setModels] = useState<string[]>([]);
    const [running, setRunning] = useState(false);

    const handleCheck = async () => {
        let detailed;
        try {
            detailed = parseDetailedJson(text);
        } catch (err: any) {
            setError(err.message);
            return;
        }
        const sorted = [...detailed.candidates].sort((a, b) => b.totalScore - a.totalScore);
        const ids = sorted.map((c) => c.contact.stContactID);
        setError(null);
        setCandidates(sorted);
        setModels([]);
        setResults(Object.fromEntries(ids.map((id) => [id, {state: 'loading'}])));
        setRunning(true);

        // Backend nimmt höchstens LLM_MAX_CANDIDATES pro Aufruf
        for (let i = 0; i < ids.length; i += LLM_MAX_CANDIDATES) {
            const chunk = ids.slice(i, i + LLM_MAX_CANDIDATES);
            let update: Record<string, LlmCandidateState>;
            try {
                const res = await Api.duplicateCheckLlmAssess({
                    target: llmPreset ? {preset: llmPreset} : undefined,
                    detailed,
                    candidateIds: chunk,
                });
                const split = splitLlmCandidates(res.assessments, chunk);
                update = Object.fromEntries(chunk.map((id) => {
                    const assessment = split[id];
                    return [id, assessment === 'missing'
                        ? {state: 'missing'}
                        : {state: 'done', assessment, model: res.target.model}];
                }));
                setModels((prev) => (prev.includes(res.target.model) ? prev : [...prev, res.target.model]));
            } catch (err: any) {
                const message = typeof err === 'string' ? err
                    : Array.isArray(err) ? err.join(', ')
                        : 'Fehler beim LLM-Aufruf';
                update = Object.fromEntries(chunk.map((id) => [id, {state: 'error', message}]));
            }
            setResults((prev) => ({...prev, ...update}));
        }
        setRunning(false);
    };

    return (
        <>
            <TextField
                fullWidth
                multiline
                minRows={8}
                maxRows={20}
                variant="outlined"
                label="Dubletten-JSON (Ergebnis der Einzelabfrage)"
                value={text}
                onChange={(e) => {
                    setText(e.target.value);
                    setError(null);
                }}
                error={!!error}
                helperText={error ?? undefined}
                inputProps={{className: classes.editor, spellCheck: false}}
            />
            <Box className={classes.actions}>
                <LlmPanel config={llmConfig} preset={llmPreset} onPresetChange={onLlmPresetChange}/>
                <Button variant="contained" color="primary" disabled={!text.trim() || running || !llmPreset} onClick={handleCheck}>
                    {running ? 'Prüfe…' : 'Prüfen'}
                </Button>
                {models.length > 0 && (
                    <Typography variant="body2" color="textSecondary">Modell: {models.join(', ')}</Typography>
                )}
            </Box>

            {candidates.length > 0 && (
                <Box mt={3}>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Kandidat</TableCell>
                                <TableCell>Regelbasiert</TableCell>
                                <TableCell>LLM</TableCell>
                                <TableCell>Konfidenz</TableCell>
                                <TableCell>Begründung</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {candidates.map((c) => (
                                <TableRow key={c.contact.stContactID}>
                                    <TableCell>
                                        <b>{c.contact.companyLine1}</b>
                                        {c.contact.companyLine2 && <div>{c.contact.companyLine2}</div>}
                                        {c.contact.sapCustomerID && (
                                            <Typography variant="caption" color="textSecondary">SAP {c.contact.sapCustomerID}</Typography>
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <StatusBadge status={c.status}/> {Math.round(c.totalScore * 100)} %
                                    </TableCell>
                                    <LlmCells result={results[c.contact.stContactID]} classes={classes}/>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}
        </>
    );
}

function LlmCells({result, classes}: {result?: LlmCandidateState; classes: ReturnType<typeof useStyles>}) {
    if (!result || result.state === 'loading') {
        return <TableCell colSpan={3}><CircularProgress size={16}/></TableCell>;
    }
    if (result.state === 'error') {
        return <TableCell colSpan={3} className={classes.errorText}>{result.message}</TableCell>;
    }
    if (result.state === 'missing') {
        return <TableCell colSpan={3}>Keine Bewertung erhalten</TableCell>;
    }
    const {assessment} = result;
    return (
        <>
            <TableCell>
                <Chip
                    label={LLM_VERDICT_LABELS[assessment.verdict] ?? assessment.verdict}
                    size="small"
                    className={classes.verdictChip}
                    style={{
                        backgroundColor: LLM_VERDICT_COLORS[assessment.verdict] ?? GREY,
                        color: assessment.verdict === 'uncertain' ? 'rgba(0,0,0,0.87)' : undefined,
                    }}
                />
                {assessment.pattern && <div>{LLM_PATTERN_LABELS[assessment.pattern] ?? assessment.pattern}</div>}
            </TableCell>
            <TableCell>{Math.round(assessment.confidence * 100)} %</TableCell>
            <TableCell className={classes.reasoning}>{assessment.reasoning}</TableCell>
        </>
    );
}
